/**
 * Downscales a user-selected image on the client before it is embedded as a
 * base64 data URL in a JSON request body.
 *
 * Base64 inflates the payload by ~33%, and lossless PNGs (screenshots in
 * particular) are routinely several megabytes, which the API gateway rejects
 * before any CORS headers are attached — surfacing in the browser as an
 * opaque CORS failure rather than a 413. Resizing here keeps the encoded
 * payload comfortably inside the gateway's body limit.
 *
 * Images that actually use their alpha channel stay PNG so transparency
 * survives; fully opaque images encode as JPEG, which is far smaller.
 */

/** Longest edge of the output image, in pixels. */
export const MAX_IMAGE_DIMENSION = 512;

/** Upper bound for the encoded data URL, measured the way it is sent. */
export const MAX_IMAGE_DATA_URL_BYTES = 512 * 1024;

/** Guard against decoding files large enough to exhaust memory on mobile. */
export const MAX_SOURCE_FILE_BYTES = 20 * 1024 * 1024;

export const OPAQUE_MIME_TYPE = 'image/jpeg';
export const TRANSPARENT_MIME_TYPE = 'image/png';

const JPEG_QUALITY_STEPS = [0.85, 0.7, 0.55, 0.4];

/**
 * PNG is lossless, so the quality argument does nothing — its only lever is
 * dimension, hence the extra fallback step compared to the JPEG path.
 */
const DIMENSION_DIVISORS = [1, 2, 4, 8];

export type ResizeImageOptions = {
    maxDimension?: number;
    maxBytes?: number;
    /** Forces an output format instead of choosing one by transparency. */
    mimeType?: string;
};

export type Dimensions = {
    width: number;
    height: number;
};

const supportsAlpha = (mimeType: string): boolean =>
    mimeType === TRANSPARENT_MIME_TYPE;

/**
 * Fits `width` x `height` inside a `maxDimension` square, preserving the
 * aspect ratio. Images already within bounds are returned untouched — we never
 * upscale, which would only add bytes without adding detail.
 */
export const scaleToFit = (
    width: number,
    height: number,
    maxDimension: number,
): Dimensions => {
    const longestEdge = Math.max(width, height);
    if (longestEdge <= maxDimension) {
        return { width, height };
    }
    const ratio = maxDimension / longestEdge;
    return {
        width: Math.max(1, Math.round(width * ratio)),
        height: Math.max(1, Math.round(height * ratio)),
    };
};

const loadImage = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Unable to read the selected image.'));
        };
        image.src = objectUrl;
    });

const drawToCanvas = (
    image: HTMLImageElement,
    { width, height }: Dimensions,
): { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D } => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Unable to process the selected image.');
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, width, height);

    return { canvas, context };
};

/**
 * True when any pixel is less than fully opaque. A PNG file extension alone
 * is not enough — most PNGs are opaque and should encode as JPEG instead.
 *
 * Reading pixels back can fail (no real 2d implementation, or a tainted
 * canvas), in which case we assume transparency so the alpha channel is never
 * silently discarded.
 */
const hasTransparency = (
    context: CanvasRenderingContext2D,
    { width, height }: Dimensions,
): boolean => {
    let pixels: Uint8ClampedArray;
    try {
        pixels = context.getImageData(0, 0, width, height).data;
    } catch {
        return true;
    }
    for (let offset = 3; offset < pixels.length; offset += 4) {
        if (pixels[offset] < 255) {
            return true;
        }
    }
    return false;
};

/**
 * Paints white *behind* what is already drawn, so an opaque-looking image
 * encoded as JPEG does not pick up black where the source had alpha.
 */
const flattenOntoWhite = (
    context: CanvasRenderingContext2D,
    { width, height }: Dimensions,
): void => {
    const previousOperation = context.globalCompositeOperation;
    context.globalCompositeOperation = 'destination-over';
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.globalCompositeOperation = previousOperation;
};

const qualityStepsFor = (mimeType: string): (number | undefined)[] =>
    mimeType === OPAQUE_MIME_TYPE ? JPEG_QUALITY_STEPS : [undefined];

/**
 * Resizes `file` to a data URL that fits within `maxDimension` and `maxBytes`,
 * retaining the original aspect ratio and any transparency. Steps quality down
 * first where the format allows it, then falls back to smaller dimensions, so
 * detail is only sacrificed when compression alone cannot meet the budget.
 */
export const resizeImage = async (
    file: File,
    options: ResizeImageOptions = {},
): Promise<string> => {
    const {
        maxDimension = MAX_IMAGE_DIMENSION,
        maxBytes = MAX_IMAGE_DATA_URL_BYTES,
    } = options;

    const image = await loadImage(file);
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;

    if (!sourceWidth || !sourceHeight) {
        throw new Error('Unable to read the selected image.');
    }

    let mimeType = options.mimeType;

    for (const divisor of DIMENSION_DIVISORS) {
        const dimension = Math.max(1, Math.round(maxDimension / divisor));
        const dimensions = scaleToFit(sourceWidth, sourceHeight, dimension);
        const { canvas, context } = drawToCanvas(image, dimensions);

        // Decided once, on the first (largest) render, then reused.
        if (!mimeType) {
            mimeType = hasTransparency(context, dimensions)
                ? TRANSPARENT_MIME_TYPE
                : OPAQUE_MIME_TYPE;
        }
        if (!supportsAlpha(mimeType)) {
            flattenOntoWhite(context, dimensions);
        }

        for (const quality of qualityStepsFor(mimeType)) {
            const dataUrl = canvas.toDataURL(mimeType, quality);
            if (dataUrl.length <= maxBytes) {
                return dataUrl;
            }
        }
    }

    throw new Error('Unable to process the selected image.');
};
